// Runtime X-ray lens specs, persisted per tenant.
//
// localStorage, deliberately, and deliberately temporary. Kernels and canvases
// are moving to the backend and these should follow the same path; the point of
// starting here is that a lens you can mount, watch, and unmount in one sitting
// proves the mount point without waiting on a schema. So the stored value is
// plain JSON with a version tag and nothing derived — the compiler rebuilds every
// function on load, which is what makes a later move to the backend a transport
// change rather than a rewrite.
//
// Same tenant-bucketing as kernelStore, for the same reason: an unscoped key put
// every tenant's lenses in one bucket, and a lens is written against one tenant's
// tool vocabulary.

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";
import { compileRuntimeLenses } from "./xrayRuntimeLens.js";

const STORAGE_KEY_PREFIX = "origin.xray.runtimeLenses.v2";
const LEGACY_STORAGE_KEY_PREFIX = "origin.xray.runtimeLenses.v1";

// Storage is resolved through a function, not read off the global directly, so a
// test can supply its own without assigning `globalThis.localStorage`. Several
// suites here own that global at module scope and never restore it; a module that
// joins the contention makes whichever file runs last the winner, which is how
// this module first broke four unrelated gate suites.
let _storageOverride = null;
function storage() {
  if (_storageOverride) return _storageOverride;
  return typeof localStorage === "undefined" ? null : localStorage;
}

/** Test-only: supply a storage backend. Pass null to fall back to the global. */
export function _setStorageForTests(s) { _storageOverride = s; }

function storageKey(tenantId) {
  discardLegacyTenantEntry(storage(), LEGACY_STORAGE_KEY_PREFIX, tenantId);
  return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* one listener's failure must not block its siblings */ }
  }
}

/** Subscribe to mounts/unmounts. Returns an unsubscribe. */
export function subscribeRuntimeLenses(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Every stored spec for a tenant, newest first. Never throws. */
export function listRuntimeLensSpecs(tenantId) {
  try {
    const key = storageKey(tenantId);
    if (!key) return [];
    const raw = storage()?.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => s && typeof s.id === "string");
  } catch {
    // A corrupt bucket reads as no lenses rather than taking the X-ray down with
    // it. The catalog is the product; a persisted spec is a convenience.
    return [];
  }
}

/**
 * Mount a spec, replacing any with the same id.
 *
 * Compiles before writing: an invalid spec is refused here rather than stored and
 * skipped on every later read, so the author sees the problems while they still
 * have the spec in front of them.
 *
 * @returns {{ok, problems: string[]}}
 */
export function saveRuntimeLensSpec(tenantId, spec, { reserved = [] } = {}) {
  const key = storageKey(tenantId);
  if (!key) return { ok: false, problems: ["verified tenant and user identity are required"] };
  const { manifests, problems } = compileRuntimeLenses([spec], { reserved });
  if (!manifests.length) return { ok: false, problems };
  const kept = listRuntimeLensSpecs(tenantId).filter((s) => s.id !== spec.id);
  try {
    storage()?.setItem(key, JSON.stringify([{ ...spec, savedAt: Date.now() }, ...kept]));
  } catch (err) {
    return { ok: false, problems: [`could not persist: ${err?.message || String(err)}`] };
  }
  notify();
  return { ok: true, problems: [] };
}

/** Unmount one spec. Returns whether it was there. */
export function deleteRuntimeLensSpec(tenantId, id) {
  const key = storageKey(tenantId);
  if (!key) return false;
  const all = listRuntimeLensSpecs(tenantId);
  const kept = all.filter((s) => s.id !== id);
  if (kept.length === all.length) return false;
  try {
    storage()?.setItem(key, JSON.stringify(kept));
  } catch {
    return false;
  }
  notify();
  return true;
}

/**
 * The stored specs compiled into manifests, for concatenation onto MANIFESTS.
 *
 * Problems come back beside the manifests rather than throwing: one broken spec
 * must not blank the runtime tier, and the host already warns on registry
 * problems the same way.
 */
export function runtimeLensManifests(tenantId, { reserved = [] } = {}) {
  return compileRuntimeLenses(listRuntimeLensSpecs(tenantId), { reserved });
}

/** Test-only: drop a tenant's bucket. */
export function _resetForTests(tenantId) {
  try { const key = storageKey(tenantId); if (key) storage()?.removeItem(key); } catch { /* noop */ }
  listeners.clear();
}
